import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  addDoc,
  doc,
  getDocs,
  updateDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import Modal from 'react-modal';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from '../images/shop_logo.png';
import { SHOP_NAME } from "../Variables.js";

Modal.setAppElement('#root');

// Number formatter for amounts with Naira (₦)
const formatAmount = (amount) => {
  return `₦${Number(amount).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

const formatAmount1 = (amount) => {
  return `N${Number(amount).toLocaleString('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

// Date formatter for DD MMM YYYY (e.g., 07 Oct 2025)
const dateFormatter = new Intl.DateTimeFormat('en-NG', {
  day: '2-digit',
  month: 'short',
  year: 'numeric'
});

// Date-time formatter for header (DD MMM YYYY, HH:MM AM/PM)
const formatDateTime = (date) => {
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

// Date formatter for filename (YYYY-MM-DD)
const formatDateForFilename = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const PettyCash = () => {
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(false);
  const [update, setUpdate] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [vouchers, setVouchers] = useState([]);
  const [filteredVouchers, setFilteredVouchers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expenseAccounts, setExpenseAccounts] = useState([]);

  const [newVoucher, setNewVoucher] = useState({ 
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    description: '',
    type: 'disbursement',
    expenseAccountId: '',
    approved: false,
    userId: user?.uid || ''
  });
  const [editingVoucher, setEditingVoucher] = useState(null);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isReplenishModalOpen, setIsReplenishModalOpen] = useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = useState(false);
  const [currentVoucher, setCurrentVoucher] = useState(null);
  const [isFetchingVouchers, setIsFetchingVouchers] = useState(true);
  const [hasFetchedVouchers, setHasFetchedVouchers] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [balance, setBalance] = useState(0);
  const [totalFloat, setTotalFloat] = useState(0);
  const [disbursements, setDisbursements] = useState(0);
  const MIN_THRESHOLD = 100; // Example minimum threshold for replenishment

  const vouchersCollectionRef = collection(db, "pettyCashVouchers");
  const accountsCollectionRef = collection(db, "accounts");
  const storeName = `${SHOP_NAME}`;

  useEffect(() => {
    if (user && userData) {
      fetchExpenseAccounts();
      fetchVouchers();
    }
  }, [user, userData]);

  useEffect(() => {
    const filtered = vouchers.filter((voucher) => {
      const searchLower = searchTerm.toLowerCase();
      const voucherDate = dateFormatter.format(new Date(voucher.date));
      return (
        voucher.description.toLowerCase().includes(searchLower) ||
        voucher.type.toLowerCase().includes(searchLower) ||
        voucherDate.toLowerCase().includes(searchLower)
      );
    });
    setFilteredVouchers(filtered);
  }, [searchTerm, vouchers]);

  useEffect(() => {
    if (vouchers.length > 0) {
      calculateDashboardMetrics();
    }
  }, [vouchers]);

  const fetchExpenseAccounts = async () => {
    if (!user?.uid) return;
    try {
      const q = query(accountsCollectionRef, where("type", "==", "Expense"));
      const data = await getDocs(q);
      const accountList = data.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setExpenseAccounts(accountList);
    } catch (error) {
      console.error("Error fetching expense accounts:", error);
      setError("Failed to fetch expense accounts");
      setShowModal(true);
    }
  };

  const fetchVouchers = async () => {
    if (!user?.uid) return;
    
    setIsFetchingVouchers(true);
    try {
      const q = query(vouchersCollectionRef);
      const data = await getDocs(q);
      const voucherList = data.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .sort((a, b) => new Date(b.date) - new Date(a.date)); // Latest first

      setVouchers(voucherList);
      setHasFetchedVouchers(true);
    } catch (error) {
      console.error("Error fetching vouchers:", error);
      setError("Failed to fetch vouchers");
      setShowModal(true);
    } finally {
      setIsFetchingVouchers(false);
    }
  };

  const calculateDashboardMetrics = () => {
    const replen = vouchers
      .filter(v => v.type === 'replenishment' && v.approved)
      .reduce((sum, v) => sum + parseFloat(v.amount), 0);
    const disb = vouchers
      .filter(v => v.type === 'disbursement' && v.approved)
      .reduce((sum, v) => sum + parseFloat(v.amount), 0);
    setTotalFloat(replen);
    setDisbursements(disb);
    setBalance(replen - disb);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewVoucher({ ...newVoucher, [name]: value });
  };

  const handleAddVoucher = async () => {
    if (!newVoucher.description.trim() || newVoucher.amount <= 0) {
      setError("Description and positive amount are required");
      setShowModal(true);
      return;
    }
    if (newVoucher.type === 'disbursement' && !newVoucher.expenseAccountId) {
      setError("Expense account is required for disbursements");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(vouchersCollectionRef, {
        ...newVoucher,
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Voucher added successfully");
      fetchVouchers();
      resetNewVoucher();
      setIsAddModalOpen(false);
    } catch (error) {
      setError("Failed to add voucher");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const handleUpdateVoucher = async () => {
    setUpdate(true);
    try {
      const voucherDoc = doc(db, "pettyCashVouchers", editingVoucher.id);
      await updateDoc(voucherDoc, newVoucher);
      setSuccessMessage("Voucher updated successfully");
      fetchVouchers();
      resetNewVoucher();
      setEditingVoucher(null);
      setIsEditModalOpen(false);
    } catch (error) {
      setError("Failed to update voucher");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const resetNewVoucher = () => {
    setNewVoucher({ 
      date: new Date().toISOString().split('T')[0],
      amount: 0,
      description: '',
      type: 'disbursement',
      expenseAccountId: '',
      approved: false,
      userId: user?.uid || ''
    });
  };

  const handleEditVoucher = (voucherToEdit) => {
    setEditingVoucher(voucherToEdit);
    setNewVoucher({ 
      date: voucherToEdit.date,
      amount: voucherToEdit.amount,
      description: voucherToEdit.description,
      type: voucherToEdit.type,
      expenseAccountId: voucherToEdit.expenseAccountId || '',
      approved: voucherToEdit.approved,
      userId: voucherToEdit.userId
    });
    setIsEditModalOpen(true);
  };

  const confirmApproveVoucher = (voucherToApprove) => {
    setCurrentVoucher(voucherToApprove);
    setIsApproveModalOpen(true);
  };

  const handleApproveVoucher = async (id) => {
    setUpdate(true);
    try {
      const voucherDoc = doc(db, "pettyCashVouchers", id);
      await updateDoc(voucherDoc, { approved: true });
      setSuccessMessage("Voucher approved successfully");
      fetchVouchers();
    } catch (error) {
      setError("Failed to approve voucher");
    } finally {
      setUpdate(false);
      setIsApproveModalOpen(false);
      setShowModal(true);
    }
  };

  const handleReplenishRequest = async () => {
    if (newVoucher.amount <= 0) {
      setError("Positive amount required for replenishment");
      setShowModal(true);
      return;
    }
    setUpdate(true);
    try {
      await addDoc(vouchersCollectionRef, {
        date: newVoucher.date,
        amount: newVoucher.amount,
        description: 'Replenishment request',
        type: 'replenishment',
        expenseAccountId: '',
        approved: false, // Pending approval
        userId: user?.uid,
        createdAt: Timestamp.now()
      });
      setSuccessMessage("Replenishment requested successfully");
      fetchVouchers();
      resetNewVoucher();
      setIsReplenishModalOpen(false);
    } catch (error) {
      setError("Failed to request replenishment");
    } finally {
      setUpdate(false);
      setShowModal(true);
    }
  };

  const validateDateRange = () => {
    if (!startDate || !endDate) {
      setError("Please select both start and end dates");
      setShowModal(true);
      return false;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start date cannot be after end date");
      setShowModal(true);
      return false;
    }
    return true;
  };

  const generatePettyCashPDF = () => {
    if (!validateDateRange()) return;

    const dateFilteredVouchers = vouchers.filter(voucher => {
      const voucherDate = new Date(voucher.date);
      return voucherDate >= new Date(startDate) && voucherDate <= new Date(endDate);
    });

    if (dateFilteredVouchers.length === 0) {
      setError("No transactions found for the selected date range");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const fullName = userData?.firstName && userData?.lastName
      ? `${userData.firstName} ${userData.lastName}`
      : user?.displayName || "Unknown";

    let currentY = 10;
    doc.addImage(Logo, "PNG", 14, currentY, 15, 15);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(storeName, 35, currentY + 5);
    doc.setFontSize(16);
    doc.setFont("helvetica", "normal");
    doc.text("Petty Cash Transactions Report", 35, currentY + 13);
    doc.setFontSize(10);
    doc.text(`Generated by: ${fullName}`, 35, currentY + 18);
    doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, currentY + 23);
    doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, currentY + 28);
    currentY += 33;

    // Sort vouchers by date (ascending)
    const sortedVouchers = [...dateFilteredVouchers].sort((a, b) => new Date(a.date) - new Date(b.date));

    const tableData = sortedVouchers.map((voucher, index) => [
      (index + 1).toString(),
      dateFormatter.format(new Date(voucher.date)),
      voucher.description,
      voucher.type.charAt(0).toUpperCase() + voucher.type.slice(1),
      voucher.type === 'disbursement' ? (expenseAccounts.find(a => a.id === voucher.expenseAccountId)?.name || '-') : '-',
      formatAmount1(voucher.amount),
      voucher.approved ? 'Approved' : 'Pending'
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['S/N', 'Date', 'Description', 'Type', 'Expense Account', 'Amount', 'Status']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [41, 128, 185] },
      bodyStyles: { textColor: [0, 0, 0] },
      margin: { left: 8, right: 10 },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 50 },
        3: { cellWidth: 22 },
        4: { cellWidth: 40 },
        5: { cellWidth: 25 },
        6: { cellWidth: 20 }
      },
      didDrawPage: (data) => {
        currentY = data.table.finalY || data.cursor.y;
        if (data.pageNumber < doc.internal.getNumberOfPages()) {
          doc.setPage(data.pageNumber + 1);
          doc.addImage(Logo, "PNG", 14, 10, 15, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text(storeName, 35, 15);
          doc.setFontSize(16);
          doc.setFont("helvetica", "normal");
          doc.text("Petty Cash Transactions Report", 35, 23);
          doc.setFontSize(10);
          doc.text(`Generated by: ${fullName}`, 35, 28);
          doc.text(`Generated on: ${formatDateTime(new Date())}`, 35, 33);
          doc.text(`Period: ${dateFormatter.format(new Date(startDate))} to ${dateFormatter.format(new Date(endDate))}`, 35, 38);
          currentY = 44;
        }
      }
    });

    currentY += 10;
    const totalAmount = sortedVouchers.reduce((sum, voucher) => sum + parseFloat(voucher.amount), 0);
    doc.setFontSize(12);
    doc.text(`Total Amount: ${formatAmount1(totalAmount)}`, 14, currentY);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 30,
        doc.internal.pageSize.height - 10
      );
    }

    doc.save(`petty_cash_transactions_${formatDateForFilename(new Date(startDate))}_to_${formatDateForFilename(new Date(endDate))}.pdf`);
    setIsDateRangeModalOpen(false);
    setStartDate('');
    setEndDate('');
  };

  if (loading) return <Loader text={"Loading"} />;
  if (!userData) return <Loader text={"Reading"} />;

  const allowedRoles = ['Cashier', 'Accountant', 'Admin', 'Supreme'];

  const displayContent = () => {
    if (isFetchingVouchers && !hasFetchedVouchers) {
      return <p className="loading-text">Loading vouchers ...</p>;
    }
    
    if (filteredVouchers.length === 0) {
      if (searchTerm) {
        return <p className="loading-text">No vouchers match your search.</p>;
      } else {
        return <p className="loading-text">No vouchers found. Add your first voucher.</p>;
      }
    }
    
    return filteredVouchers.map((voucherItem) => (
      <div key={voucherItem.id} className="class-item">
        <div className="column gap-1">
          <p><strong>{voucherItem.description}</strong><br />
            <span>Type: {voucherItem.type}</span><br />
            <span>Amount: {formatAmount(voucherItem.amount)}</span><br />
            <span>Date: {dateFormatter.format(new Date(voucherItem.date))}</span><br />
            {voucherItem.expenseAccountId && <span>Expense Account: {expenseAccounts.find(a => a.id === voucherItem.expenseAccountId)?.name || 'Unknown'}</span>}<br />
            <span className="size5 font-light-blue-cash bold">{voucherItem.approved ? 'Approved' : 'Pending'}</span>
          </p>                        
        </div>
        <div className="row align-center justify-center gap-15">
          <button className="list-btn" onClick={() => handleEditVoucher(voucherItem)}>
            <i className="fas fa-edit"></i>
          </button>
          {!voucherItem.approved && (
            <button className="list-btn" onClick={() => confirmApproveVoucher(voucherItem)}>
              <i className="fas fa-check"></i>
            </button>
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {update && <Loader text={"Processing"} />}

      {allowedRoles.includes(userData.status) ? (
        <>
          <div className="top-elements row align-center space-between">
            <Link to="/home" className="back-link">
              <i className="fa-solid fa-arrow-left size3"></i>
            </Link>
            <h2 className="manage-page-heading caps size4 margin-top-cashflow1">Petty Cash</h2>
            <Link to="#" className="home-link" onClick={() => fetchVouchers()}>
              <i className="fa-solid fa-rotate-right size4"></i>
            </Link>
          </div>

          <div className="login-page-items login-page-items-cashflow column align-center scroll-fixed scroll-fixed6">
            {/* Custodian Dashboard */}
            <div className="dashboard-section column align-center gap-1">
              <p>
                <span className="bold font-blue-cash">Total Float: {formatAmount(totalFloat.toFixed(2))}</span><br />
                <span className="bold font-blue-cash">Disbursements: {formatAmount(disbursements.toFixed(2))}</span><br />
                <span className="bold font-orange">Current Balance: {formatAmount(balance.toFixed(2))}</span>
              </p>                            
            </div>    

            <div className="row justify-center align-center gap-15 add-buttons margin-top-cashflow1" style={{alignItems: 'center'}}>
              {balance < MIN_THRESHOLD && (
                <button className="login-button login-button-d caps" onClick={() => setIsReplenishModalOpen(true)}>Request Replenishment</button>
              )}
              <button className="login-button login-button-d caps" onClick={() => setIsAddModalOpen(true)}>Add Voucher</button>
              <button className="login-button login-button-d caps" onClick={() => setIsDateRangeModalOpen(true)}>Download PDF</button>
            </div>

            <input
              type="text"
              className="input-field1"
              placeholder="Search by description, type, or date"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <div className="scroll-fixed scroll-fixed1">
              <div className="class-list class-list-d scrollable">
                {displayContent()}
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="male-female-stat align-center justify-center">You don't have permission to view this page.</p>
      )}

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={isAddModalOpen}
        onRequestClose={() => setIsAddModalOpen(false)}
        contentLabel="Add Voucher"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Add Petty Cash Voucher</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="date" className="input-label">Date</label>  
          <input
            type="date"
            name="date"
            className="input-field"
            value={newVoucher.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="type" className="input-label">Type</label>
          <select
            name="type"
            className="input-field"
            value={newVoucher.type}
            onChange={handleInputChange}
          >
            <option value="disbursement">Disbursement</option>
            <option value="replenishment">Replenishment</option>
          </select>
          <label htmlFor="amount" className="input-label">Amount</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newVoucher.amount}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="description" className="input-label">Description</label>
          <input
            type="text"
            name="description"
            className="input-field"
            placeholder="Description"
            value={newVoucher.description}
            onChange={handleInputChange}
            required
          />
          {newVoucher.type === 'disbursement' && (
            <>
              <label htmlFor="expenseAccountId" className="input-label">Expense Account</label>
              <select
                name="expenseAccountId"
                className="input-field"
                value={newVoucher.expenseAccountId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Expense Account</option>
                {expenseAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </>
          )}
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleAddVoucher}>Add Voucher</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen}
        onRequestClose={() => {
          setIsEditModalOpen(false);
          resetNewVoucher();
          setEditingVoucher(null);
        }}
        contentLabel="Edit Voucher"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Edit Petty Cash Voucher</h2>
        <form className="session-form" style={{ maxHeight: '500px', overflowY: 'auto' }}>
          <label htmlFor="date" className="input-label">Date</label>
          <input
            type="date"
            name="date"
            className="input-field"
            value={newVoucher.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="type" className="input-label">Type</label>
          <select
            name="type"
            className="input-field"
            value={newVoucher.type}
            onChange={handleInputChange}
          >
            <option value="disbursement">Disbursement</option>
            <option value="replenishment">Replenishment</option>
          </select>
          <label htmlFor="amount" className="input-label">Amount</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Amount"
            value={newVoucher.amount}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="description" className="input-label">Description</label>
          <input
            type="text"
            name="description"
            className="input-field"
            placeholder="Description"
            value={newVoucher.description}
            onChange={handleInputChange}
            required
          />
          {newVoucher.type === 'disbursement' && (
            <>
              <label htmlFor="expenseAccountId" className="input-label">Expense Account</label>
              <select
                name="expenseAccountId"
                className="input-field"
                value={newVoucher.expenseAccountId}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Expense Account</option>
                {expenseAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>{acc.name}</option>
                ))}
              </select>
            </>
          )}
          <label>
            <input
              type="checkbox"
              name="approved"
              checked={newVoucher.approved}
              onChange={(e) => setNewVoucher({ ...newVoucher, approved: e.target.checked })}
            />
            Approved
          </label>
          <div className="row align-center justify-center gap-1">
            <button className="login-button" type="button" onClick={() => {
              setIsEditModalOpen(false);
              resetNewVoucher();
              setEditingVoucher(null);
            }}>Cancel</button>
            <button className="login-button" type="button" onClick={handleUpdateVoucher}>Update Voucher</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isApproveModalOpen}
        onRequestClose={() => setIsApproveModalOpen(false)}
        contentLabel="Approve Voucher"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Approve Voucher</h2>
        {currentVoucher && (
          <div>
            <p>Are you sure you want to approve <strong>{currentVoucher.description}</strong> for {formatAmount(currentVoucher.amount)}?</p>
            <div className="row align-center gap-1">
              <button className="login-button" type="button" onClick={() => setIsApproveModalOpen(false)}>Cancel</button>
              <button className="login-button" type="button" onClick={() => handleApproveVoucher(currentVoucher.id)}>Approve</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isReplenishModalOpen}
        onRequestClose={() => setIsReplenishModalOpen(false)}
        contentLabel="Request Replenishment"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Request Petty Cash Replenishment</h2>
        <form className="session-form">
          <label htmlFor="date" className="input-label">Date</label>  
          <input
            type="date"
            name="date"
            className="input-field"
            value={newVoucher.date}
            onChange={handleInputChange}
            required
          />
          <label htmlFor="amount" className="input-label">Amount</label>
          <input
            type="number"
            name="amount"
            className="input-field"
            placeholder="Replenishment Amount"
            value={newVoucher.amount}
            onChange={handleInputChange}
            required
          />
          <div className="row align-center gap-1">
            <button className="login-button" type="button" onClick={() => setIsReplenishModalOpen(false)}>Cancel</button>
            <button className="login-button" type="button" onClick={handleReplenishRequest}>Request</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDateRangeModalOpen}
        onRequestClose={() => {
          setIsDateRangeModalOpen(false);
          setStartDate('');
          setEndDate('');
        }}
        contentLabel="Select Date Range"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <h2>Select Date Range for PDF</h2>
        <form className="session-form">
          <label className="input-label">Start Date</label>
          <input
            type="date"
            className="input-field"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
          <label className="input-label">End Date</label>
          <input
            type="date"
            className="input-field"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
          <div className="row align-center gap-1">
            <button
              className="login-button"
              type="button"
              onClick={() => {
                setIsDateRangeModalOpen(false);
                setStartDate('');
                setEndDate('');
              }}
            >
              Cancel
            </button>
            <button
              className="login-button"
              type="button"
              onClick={generatePettyCashPDF}
            >
              Generate PDF
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default PettyCash;